/**
 * The Overview's one reading of the system, shared by the tiles, the hero flow and the status band
 * so they count the same way: sessions, projects, pipelines, the landing queue and what needs you.
 * Also the Overview's Pause live: it holds the picture's motion and the activity list still (the
 * numbers keep their latest value), remembered across visits.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import type { Incident } from '@orgus/protocol';
import { useNow } from '@orgus/ui';
import { useAllSessions, useOrgus } from '../../lib/store.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { useLanding } from '../landing/api.ts';
import { useWatch } from '../watch/useWatch.ts';
import { useMissionsState } from '../missions/data.ts';
import type { FlowInput, FlowMission } from './flow.ts';

export function useHeroInput(): { input: FlowInput; landingPhase: 'loading' | 'ready' | 'error' } {
  const sessions = useAllSessions();
  const projects = useOrgus((s) => s.projects);
  const pipelinesMap = useOrgus((s) => s.pipelines);
  const { state: landing, phase } = useLanding();
  const needs = useNeedsYouState().bySession;
  const now = useNow(30_000);
  const pipelines = useMemo(() => Object.values(pipelinesMap), [pipelinesMap]);
  // Missions Orgus landed itself count as landings on their project's main (QA-42).
  const missionsState = useMissionsState().state;
  const missions = useMemo<FlowMission[]>(
    () => (missionsState?.missions ?? []).map((m) => ({ id: m.id, projectId: m.projectId, state: m.state, finishedAt: null, landedAt: m.integration?.status === 'landed' ? m.integration.at : null })),
    [missionsState],
  );
  const input = useMemo<FlowInput>(() => ({ sessions, projects, pipelines, landing, needs, missions, now }), [sessions, projects, pipelines, landing, needs, missions, now]);
  return { input, landingPhase: phase };
}

/** The watch's incidents, empty until it answers (main's health reads them). */
export function useIncidents(): readonly Incident[] {
  const w = useWatch();
  return w.kind === 'ready' ? w.state.incidents : EMPTY;
}
const EMPTY: readonly Incident[] = [];

const PAUSE_KEY = 'orgus.overview.paused';
const readPaused = () => {
  try {
    return localStorage.getItem(PAUSE_KEY) === '1';
  } catch {
    return false;
  }
};

export const useOverviewPause = create<{ paused: boolean; toggle: () => void }>()((set, get) => ({
  paused: readPaused(),
  toggle: () => {
    const paused = !get().paused;
    set({ paused });
    try {
      localStorage.setItem(PAUSE_KEY, paused ? '1' : '0');
    } catch {
      // Storage blocked: the choice lasts for this visit.
    }
  },
}));
